import { useState } from "react";
import { Trash2, Star, UserSquare2, Pencil, Check } from "lucide-react";
import { photoUrl } from "../../lib/usePortfolio";
import { CATEGORIES } from "./AdminPortfolio";

const miniBtn =
  "inline-flex items-center gap-1.5 border px-2.5 py-1.5 text-[9px] font-mono tracking-[0.15em] uppercase transition-colors";

export default function PhotoCard({ photo: p, onUpdate, onDelete }) {
  const [editing, setEditing] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [draft, setDraft] = useState({ title: p.title, location: p.location, story: p.story, category: p.category });

  const save = () => {
    onUpdate(p.id, draft);
    setEditing(false);
  };

  return (
    <article data-testid={`photo-card-${p.id}`} className="border border-white/10 bg-elevated overflow-hidden group">
      <div className="relative aspect-[4/3] overflow-hidden bg-surface">
        <img src={photoUrl(p)} alt={p.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
        {p.placement && (
          <span data-testid={`photo-placement-${p.id}`} className="absolute top-2 left-2 bg-gold text-obsidian px-2 py-1 text-[9px] font-mono tracking-[0.2em] uppercase">
            {p.placement === "hero" ? "Hero" : "About"}
          </span>
        )}
        <span className="absolute bottom-2 right-2 bg-black/60 px-2 py-1 text-[9px] font-mono tracking-[0.2em] uppercase text-stone-300">{p.aspect}</span>
      </div>

      <div className="p-4">
        {editing ? (
          <div className="space-y-3">
            <select data-testid={`photo-edit-category-${p.id}`} value={draft.category} onChange={(e) => setDraft({ ...draft, category: e.target.value })} className="w-full bg-transparent border-b border-white/15 py-1.5 text-xs text-ivory outline-none">
              {CATEGORIES.map((c) => <option key={c} value={c} className="bg-surface">{c}</option>)}
            </select>
            <input data-testid={`photo-edit-title-${p.id}`} value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} className="w-full bg-transparent border-b border-white/15 py-1.5 text-xs text-ivory outline-none" placeholder="Title" />
            <input data-testid={`photo-edit-location-${p.id}`} value={draft.location} onChange={(e) => setDraft({ ...draft, location: e.target.value })} className="w-full bg-transparent border-b border-white/15 py-1.5 text-xs text-ivory outline-none" placeholder="Location" />
            <textarea data-testid={`photo-edit-story-${p.id}`} rows={2} value={draft.story} onChange={(e) => setDraft({ ...draft, story: e.target.value })} className="w-full bg-transparent border-b border-white/15 py-1.5 text-xs text-ivory outline-none resize-none" placeholder="Story" />
            <button data-testid={`photo-edit-save-${p.id}`} onClick={save} className={`${miniBtn} border-champagne text-champagne hover:bg-champagne hover:text-obsidian`}>
              <Check size={11} /> Save
            </button>
          </div>
        ) : (
          <>
            <p className="text-[9px] font-mono tracking-[0.2em] uppercase text-champagne">{p.category}{p.location && ` · ${p.location}`}</p>
            <h3 className="mt-1 font-serif text-base text-ivory leading-tight truncate">{p.title}</h3>
            {p.story && <p className="mt-1 text-xs font-light text-stone-400 line-clamp-2">{p.story}</p>}
          </>
        )}

        <div className="mt-4 flex flex-wrap gap-1.5">
          <button data-testid={`photo-edit-${p.id}`} onClick={() => setEditing((v) => !v)} className={`${miniBtn} border-white/15 text-stone-300 hover:border-champagne hover:text-champagne`}>
            <Pencil size={11} /> {editing ? "Cancel" : "Edit"}
          </button>
          {p.placement === "hero" ? (
            <button data-testid={`photo-unset-hero-${p.id}`} onClick={() => onUpdate(p.id, { clear_placement: true })} className={`${miniBtn} border-gold text-gold hover:bg-gold hover:text-obsidian`}>
              <Star size={11} /> Unset Hero
            </button>
          ) : (
            <button data-testid={`photo-set-hero-${p.id}`} onClick={() => onUpdate(p.id, { placement: "hero" })} className={`${miniBtn} border-white/15 text-stone-300 hover:border-gold hover:text-gold`}>
              <Star size={11} /> Hero
            </button>
          )}
          {p.placement === "about" ? (
            <button data-testid={`photo-unset-about-${p.id}`} onClick={() => onUpdate(p.id, { clear_placement: true })} className={`${miniBtn} border-gold text-gold hover:bg-gold hover:text-obsidian`}>
              <UserSquare2 size={11} /> Unset About
            </button>
          ) : (
            <button data-testid={`photo-set-about-${p.id}`} onClick={() => onUpdate(p.id, { placement: "about" })} className={`${miniBtn} border-white/15 text-stone-300 hover:border-gold hover:text-gold`}>
              <UserSquare2 size={11} /> About
            </button>
          )}
          {confirming ? (
            <button data-testid={`photo-delete-confirm-${p.id}`} onClick={() => onDelete(p.id)} className={`${miniBtn} border-terracotta text-terracotta hover:bg-terracotta hover:text-obsidian`}>
              Confirm
            </button>
          ) : (
            <button data-testid={`photo-delete-${p.id}`} onClick={() => setConfirming(true)} className={`${miniBtn} border-white/15 text-stone-500 hover:border-terracotta hover:text-terracotta`} aria-label="Delete photo">
              <Trash2 size={11} />
            </button>
          )}
        </div>
      </div>
    </article>
  );
}
